import { SharesSection } from '@/features/sharing/SharesSection'
import { pageClassName } from '@/components/ui/Page'
import { ChevronRight, Cloud, HardDrive, Info, Link2, Palette, Shield, SlidersHorizontal, UserRound } from 'lucide-react'
import { useEffect, type ReactNode } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router'

import { useMe, useSettings, useStorage } from '@/api/hooks'
import { formatBytes } from '@/lib/format'
import { useIncognito } from '@/lib/incognito'
import { useTheme } from '@/lib/theme'

import { AboutSection } from './AboutSection'
import { AccountSection } from './AccountSection'
import { AppearanceSection } from './AppearanceSection'
import { HistorySection } from './HistorySection'
import { IncognitoSection } from './IncognitoSection'
import { LibrarySection } from './LibrarySection'
import { ProvidersSection } from './ProvidersSection'
import { StorageSection } from './StorageSection'
import { SettingsBackLink } from './SettingsBackLink'

const SECTIONS = [
  { id: 'library', label: 'Playback', icon: SlidersHorizontal, group: 'Music' },
  { id: 'sharing', label: 'Shared links', icon: Link2, group: 'Music' },
  { id: 'storage', label: 'Storage', icon: HardDrive, group: 'Music' },
  { id: 'providers', label: 'Providers & metadata', icon: Cloud, group: 'Music' },
  { id: 'listening', label: 'Privacy', icon: Shield, group: 'Personal' },
  { id: 'appearance', label: 'Appearance', icon: Palette, group: 'Personal' },
  { id: 'account', label: 'Account', icon: UserRound, group: 'Personal' },
  { id: 'about', label: 'About Tunes', icon: Info, group: 'About' },
] as const

type SectionId = (typeof SECTIONS)[number]['id']

const CONTENT = {
  sharing: <SharesSection />,
  library: <LibrarySection />,
  storage: <StorageSection />,
  listening: <><IncognitoSection /><HistorySection /></>,
  providers: <ProvidersSection />,
  appearance: <AppearanceSection />,
  account: <AccountSection />,
  about: <AboutSection />,
} satisfies Record<SectionId, ReactNode>

export function SettingsPage() {
  const [params] = useSearchParams()
  const location = useLocation()
  const { data: settings } = useSettings()
  const { data: user } = useMe()
  const { data: storage } = useStorage()
  const incognito = useIncognito((state) => state.active)
  const { theme } = useTheme()
  // Old Deezer bookmarklets still open the appropriate detail page.
  const requested = /(?:^|[#&])arl=/.test(location.hash) ? 'providers' : params.get('section')
  const active = SECTIONS.find(({ id }) => id === requested)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [active?.id])

  const summaries: Partial<Record<SectionId, string>> = {
    library: settings ? `${settings.playback_cache_days}-day cache` : '',
    storage: storage ? formatBytes(storage.quota_used_bytes ?? storage.used_bytes) : '',
    listening: incognito ? 'Incognito on' : 'Incognito off',
    appearance: { system: 'System', light: 'Light', dark: 'Dark' }[theme],
    account: user?.display_name,
  }

  return (
    <div className={pageClassName('form')}>
      {active ? (
        <header className="mb-4">
          <SettingsBackLink />
          <h1 className="sr-only">{active.label} settings</h1>
        </header>
      ) : (
        <header className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight text-text">Settings</h1>
        </header>
      )}

      <nav aria-label="Settings" hidden={!!active} className={active ? 'hidden' : 'space-y-6'}>
        {(['Music', 'Personal', 'About'] as const).map((group) => (
          <div key={group}>
            {group !== 'About' ? <h2 className="mb-2 px-1 text-xs font-medium text-text-muted">{group}</h2> : null}
            <ul className="overflow-hidden rounded-card border border-border bg-surface-raised divide-y divide-border">
              {SECTIONS.filter((section) => section.group === group).map(({ id, label, icon: Icon }) => (
                <li key={id}>
                  <Link to={`/settings?section=${id}`} className="flex min-h-16 items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-hover">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent">
                      <Icon className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <span className="shrink-0 text-sm font-medium text-text">{label}</span>
                    <span className="ml-auto truncate text-right text-xs text-text-muted">{summaries[id]}</span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-text-subtle" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Retain form drafts and install/credential listeners while only showing
          the selected page. The overview itself contains no form controls. */}
      {SECTIONS.map(({ id }) => (
        <div key={id} hidden={active?.id !== id} className={active?.id === id ? 'space-y-4' : 'hidden'}>
          {CONTENT[id]}
        </div>
      ))}
    </div>
  )
}
